import { useCallback, useEffect, useMemo, useRef } from 'react'
import classNames from 'classnames'

import Button from '@/components/base/Button'
import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Image from '@/components/base/Image'
import ImageParallax from '@/components/base/ImageParallax'
import Motion from '@/components/base/Motion'
import Typography, { TypographyProps } from '@/components/base/Typography'
import useInView from '@/hooks/useInView'
import { useLayoutStore } from '@/store/layout'

import { heroRevealAnimation } from './animations'
import { Section, WPImage, WPLink } from '@/types'

type HeroProps = Section &
  Partial<{
    theme: 'light' | 'dark'
    image: WPImage
    image_mode: 'fullscreen' | 'circle'
    title_variant: TypographyProps['variant']
    title_component: TypographyProps['component']
    title: string
    overline: string
    paragraph: string
    add_bottom_logos: boolean
    logos: WPImage[]
    button: WPLink
  }>

const Hero = ({
  theme,
  image,
  image_mode,
  overline,
  title,
  title_variant,
  title_component,
  paragraph,
  button,
  add_bottom_logos,
  logos,
  className,
  ...props
}: HeroProps) => {
  const setCurrentLayout = useLayoutStore((state) => state.setCurrentLayout)
  const ref = useRef<HTMLDivElement>(null)

  const needsFullscreenImage = useMemo(
    () => image && image.url && image_mode === 'fullscreen',
    [image, image_mode]
  )

  const getScrollTriggerEnd = useCallback(() => {
    const bbox = document.getElementById('menu-button')?.getBoundingClientRect()
    return bbox ? `bottom +=${bbox.top + bbox.height / 2}px` : 'bottom top'
  }, [])

  const isInView = useInView({
    ref,
    initialState: true,
    end: getScrollTriggerEnd
  })

  useEffect(() => {
    if (!needsFullscreenImage) return
    setCurrentLayout({ header: { theme: isInView ? 'light' : 'dark' } })
  }, [isInView, needsFullscreenImage, setCurrentLayout])

  return (
    <Motion nodeRef={ref} animation={heroRevealAnimation}>
      {() => (
        <section
          ref={ref}
          className={classNames(
            'relative w-full',
            className,
            {
              'h-svh-vh': needsFullscreenImage,
              'hero-h-svh-vh': !needsFullscreenImage
            },
            {
              'bg-blue': needsFullscreenImage
            }
          )}
          {...props}
        >
          {needsFullscreenImage ? (
            <div
              className={classNames(
                'absolute left-0 top-0 z-0 h-full w-full',
                'before:absolute before:left-0 before:top-0 before:h-full before:w-full before:rounded-xs before:bg-blue',
                {
                  'after:absolute after:left-0 after:top-0 after:h-full after:w-full after:bg-black/40':
                    theme === 'light'
                }
              )}
            >
              <ImageParallax fill data-motion-parallax>
                {(ref) => (
                  <Image
                    ref={ref}
                    size="large"
                    priority={true}
                    source={image}
                    alt={image?.alt}
                    fill
                  />
                )}
              </ImageParallax>
            </div>
          ) : null}

          <Container className="relative z-10 flex h-full">
            <Grid className="items-center">
              {image && image_mode === 'circle' ? (
                <div className="col-span-2 mb-[4rem] mt-[8rem] md:order-2 md:col-span-3 md:m-0 lg:col-span-6 lg:p-2xl">
                  <ImageParallax className="rounded-full" data-motion-parallax>
                    {(ref) => (
                      <Image
                        ref={ref}
                        ratio="1:1"
                        size="large"
                        loading="lazy"
                        source={image}
                        alt={image?.alt}
                        className="rounded-full"
                      />
                    )}
                  </ImageParallax>
                </div>
              ) : null}

              <div className="col-span-2 md:order-1 md:col-span-3 lg:col-span-5 lg:col-start-2">
                <div className="grid gap-md">
                  {overline ? (
                    <Typography
                      variant="overline"
                      paragraph
                      className={classNames({
                        'text-blue': theme === 'dark',
                        'text-white': theme === 'light'
                      })}
                      data-motion-overline
                    >
                      {overline}
                    </Typography>
                  ) : null}

                  {title ? (
                    <Typography
                      variant={title_variant}
                      component={title_component}
                      className={classNames({
                        'text-blue [&_em]:text-pink': theme === 'dark',
                        'text-white': theme === 'light'
                      })}
                      data-motion-title
                    >
                      {title}
                    </Typography>
                  ) : null}

                  {paragraph ? (
                    <Typography
                      variant="body"
                      paragraph
                      className={classNames({
                        'text-blue': theme === 'dark',
                        'text-white': theme === 'light'
                      })}
                      data-motion-paragraph
                    >
                      {paragraph}
                    </Typography>
                  ) : null}

                  {button ? (
                    <div data-motion-button>
                      <Button href={button?.url} link variant="contained" dark>
                        {button?.title}
                      </Button>
                    </div>
                  ) : null}
                </div>
              </div>
            </Grid>
          </Container>
          {add_bottom_logos ? (
            <div className="container absolute bottom-14 left-0 right-0 z-10 flex max-w-full items-center justify-end gap-x-sm">
              {logos?.map((logo, index) => (
                <div
                  className="min-w- relative z-10 h-[37px]"
                  key={index}
                  data-motion-logo
                >
                  <Image
                    size="large"
                    priority={true}
                    source={logo}
                    alt={logo?.alt}
                    className="h-full w-auto"
                  />
                </div>
              ))}
            </div>
          ) : null}
        </section>
      )}
    </Motion>
  )
}

export default Hero
